import styles from './template.css';
import template from './template';
import {storeInstance} from '@aofl/store/dist/legacy';
import {AoflElement, customElement, property} from '@aofl/element';
import {mapStatePropertiesMixin} from '@aofl/map-state-properties-mixin';
import {i18nMixin} from '@aofl/i18n-mixin';

import translations from '@modules/i18n';
import {sdoNs, licenseActions} from '../constants-enumerate';
import {ApiService} from '../api-service';
import './inventory-updates-log-sdo';
import {Toast} from '../toast';
import {userJobs} from '../user-jobs';

const setTranslations = (ctx) => {
  const t10s = {
    toasts: {
      export_queued: ctx.__('<tt-toUmssZp>', 'Export has been queued.'),
    },
    fields: [
      {
        key: 'timestamp',
        label: ctx.__('<tt-o+RDarIO>', 'Date'),
      },
      {
        key: 'license_name',
        label: ctx.__('<tt-WaDdVjb9>', 'License Name'),
      },
      {
        key: 'user_name',
        label: ctx.__('<tt-0OsbPRUa>', 'Updated By'),
      },
      {
        key: 'from_group_name',
        label: ctx.__('<tt-LZ7eI646>', 'From'),
      },
      {
        key: 'to_group_name',
        label: ctx.__('<tt-Ef23UX18>', 'To'),
      },
      {
        key: 'event_type',
        label: ctx.__('<tt-i/QGNxJo>', 'Event'),
      },
      {
        key: 'home_access_type',
        label: ctx.__('<tt-Fw8Ld6sq>', 'Home Access Type'),
      },
      {
        key: 'product',
        label: ctx.__('<tt-dHzuuB3g>', 'Product'),
      }
    ],
    licenseTranslations: {
      [licenseActions.LICENSE_REVOKED]: ctx.isThirdPartyConnected ? ctx.__('<tt-68CIpVW1>', 'Automated Roster Sync (Clever)') : ctx.__('<tt-jAvTCTjJ>', 'Revoked'),
      [licenseActions.LICENSE_DEACTIVATED]: ctx.__('<tt-kevjKl77>', 'Revoked'),
      [licenseActions.LICENSE_EXPIRED]: ctx.__('<tt-aoc+BweW>', 'Expired'),
      [licenseActions.LICENSE_CREATED]: ctx.__('<tt-mL9965xH>', 'Assigned'),
      [licenseActions.LICENSE_ASSIGNED]: ctx.isThirdPartyConnected ? ctx.__('<tt-jbZg6kiI>', 'Automated Roster Sync (Clever)') : ctx.__('<tt-w398q+aU>', 'Assigned'),
      [licenseActions.LICENSE_EXTENDED]: ctx.__('<tt-mwyXwmRa>', 'Extended'),
      [licenseActions.LICENSE_TRANSFERRED]: ctx.__('<tt-GFPsmGcj>', 'Transferred'),
      [licenseActions.LICENSE_TAKEN]: ctx.__('<tt-XtYBiytv>', 'Taken'),
      [licenseActions.LICENSE_GIVEN]: ctx.__('<tt-szjr4aoq>', 'Given'),
      'Students': ctx.__('<tt-eLU22abd>', 'Students')
    }
  };
  if (!ctx['for-student-view']) {
    t10s.fields.push({
      key: 'quantity',
      label: ctx.__('<tt-7RTNhiO3>', 'Number of Licenses'),
    });
  }

  return t10s;
};

/**
 * @summary InventoryUpdatesLog
 * @extends {mapStatePropertiesMixin(i18nMixin(AoflElement))}
 */
@customElement('inventory-updates-log')
class InventoryUpdatesLog extends mapStatePropertiesMixin(i18nMixin(AoflElement)) {
  /**
   * Creates an instance of InventoryUpdatesLog.
   */
  constructor() {
    super();
    this.translations = translations;
    this.storeInstance = storeInstance;
  }

  @property({type: Number, mapState: `${sdoNs.INVENTORY_UPDATE_LOG}.$numPages`, store: storeInstance})
  pages = 0;

  @property({type: Number, mapState: `${sdoNs.INVENTORY_UPDATE_LOG}.$pageNumber`, store: storeInstance})
  currentPage = 0;

  @property({type: Boolean, attribute: false})
  loading = true;

  @property({type: Object, mapState: `${sdoNs.LICENSE_HISTORY}.queryObj.filters`, store: storeInstance})
  filters = {};

  @property({type: Boolean})
  ['hide-pagination'] = false;

  @property({type: Boolean})
  ['for-student-view'] = false;

  @property({type: Number, mapState: `${sdoNs.INVENTORY_UPDATE_LOG}`, store: storeInstance})
  data = {};

  @property({type: Number, mapState: `${sdoNs.SESSION}.group_info.id`, store: storeInstance})
  groupId = 0;

  @property({type: Boolean, mapState: `${sdoNs.ORG_GROUP_INFO}.is_third_party_connected`, store: storeInstance})
  isThirdPartyConnected = false;

  @property({type: Array})
  fields = [];

  @property({type: Object})
  licenseTranslations = {};

  @property({type: Array})
  openLicenseIds = {};

  connectedCallback() {
    super.connectedCallback();

    var t10s = setTranslations(this);
    for (let index in t10s) {
      this[index] = t10s[index];
    }
  }

  /**
   * @readonly
   */
  static is = 'inventory-updates-log';

  pageChanged(e) {
    const currentPage = e.detail.pageNumber;
    const lastValues = currentPage > this.currentPage ? this.data.$next : this.data.$prev;
    const sort = {
      ...this.data.sort,
      last_values: [lastValues.timestamp, lastValues.id],
      is_forward: currentPage > this.currentPage
    };

    storeInstance.commit({
      namespace: sdoNs.INVENTORY_UPDATE_LOG,
      mutationId: 'setSort',
      payload: sort,
    });

    this.dispatchEvent(new CustomEvent('history-page-changed', {}));
  }

  loadData(data) {
    this.loading = true;
    this.performUpdate();
    storeInstance.commit({
      namespace: sdoNs.INVENTORY_UPDATE_LOG,
      mutationId: 'setData',
      payload: data
    });

    this.loading = false;
    this.requestUpdate();
  }

  async export(filters, searchType, selectionMode = '') {
    const promises = this.fields.map((item) => item.label);
    const results = await Promise.all(promises);
    const fields = this.fields.map((item, index) => {
      return {
        key: item.key,
        label: results[index].strings[0]
      };
    });
    const translations = {};
    const translationResults = await Promise.all(Object.values(this.licenseTranslations));
    let index = 0;
    for (const [key, value] of Object.entries(this.licenseTranslations)) {
      translations[key] = translationResults[index].strings[0];
      index++;
    }
    const args = {
      searchType,
      search: {},
      filters,
      sort:this.data.sort,
      fields,
      filterIds: [],
      notSelected: [],
      selectionMode,
      i18n: translations
    };

    await ApiService.exportSearch(args);
    await userJobs.getJobs();
    Toast.success(this.toasts.export_queued);
  }

  toggleOpen(licenseId) {
    if (this.openLicenseIds[licenseId] === 'undefined') {
      this.openLicenseIds[licenseId] = true;
    } else {
      this.openLicenseIds[licenseId] = !this.openLicenseIds[licenseId];
    }
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default InventoryUpdatesLog;
